/**
 * OrderProductPage - Product Configuration with Immersive Visual Builder
 * 
 * Two layout modes:
 * 1. Standard mode (Quick Order, Expert) - Shows full order shell with inputs
 * 2. Immersive mode (Visual Builder) - Compact header, full-screen configurator
 * 
 * The Visual Builder should feel like a Tesla/Apple configurator - focused,
 * immersive, and product-first.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { ordersApi } from '../lib/api';
import { Header } from '../components/Header';
import { LoadingSpinner } from '../components/LoadingSpinner';
import ProductConfigWizard from '../components/ProductConfigWizard';
import CompactOrderHeader from '../components/CompactOrderHeader';
import CatalogueNumberInput from '../components/CatalogueNumberInput';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Trash2, Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

export default function OrderProductPage() {
    const { orderId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useTranslation();
    
    // Check if this is a new order (no orderId or orderId === 'new')
    const isNewOrder = !orderId || orderId === 'new';
    
    // Check if edit mode is explicitly requested via query param
    const isEditMode = searchParams.get('edit') === 'true';

    // Product family for NEW orders comes from ?family= (e.g. /orders/new/product?family=bluray).
    // For existing orders it's read off the loaded order below. Falls back to
    // vinyl so the historical /orders/new/product URL keeps working unchanged.
    const familyFromQuery = (searchParams.get('family') || '').toLowerCase();
    const [productFamily, setProductFamily] = useState(
        isNewOrder && ['vinyl', 'cd', 'dvd', 'bluray'].includes(familyFromQuery)
            ? familyFromQuery
            : 'vinyl'
    );
    
    // Ref for title input field
    const titleInputRef = useRef(null);
    
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(!isNewOrder); // Don't show loading for new orders
    const [editVinylSpecs, setEditVinylSpecs] = useState({
        format: '12"',
        quantity: 500,
        speed: '33 RPM',
        color: 'Black',
        vinyl_weight_g: 140,
        test_pressings: 0
    });
    const [isSaving, setIsSaving] = useState(false);
    
    // Track selected configuration mode and immersive state
    const [configMode, setConfigMode] = useState(null); // null = mode selection, 'quick', 'visual', 'expert'
    const isImmersiveMode = configMode === 'visual';
    
    // Delete draft order state
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    
    // Order details state. Title is mandatory; artist is optional and stored
    // separately (replaces the legacy combined "Artist - Title" input).
    const [orderTitle, setOrderTitle] = useState('');
    const [orderArtist, setOrderArtist] = useState('');
    const [catalogueNumber, setCatalogueNumber] = useState('');
    const [catalogueValid, setCatalogueValid] = useState(true);
    const [customerPoNumber, setCustomerPoNumber] = useState('');
    // Order-level production note (optional). Shown in the Quick Order configure
    // step and persisted to the order `notes` field (same one admin/production see).
    const [orderNotes, setOrderNotes] = useState('');

    useEffect(() => {
        if (!isNewOrder) {
            fetchOrder();
        }
    }, [orderId, isNewOrder]);

    const fetchOrder = async () => {
        try {
            const response = await ordersApi.getOne(orderId);
            setOrder(response.data);
            // Prefer the order's stored product_type over any ?family= override.
            if (response.data.product_type) {
                setProductFamily(response.data.product_type);
            }
            
            // Initialize order details
            setOrderTitle(response.data.title || '');
            setOrderArtist(response.data.artist || '');
            setCatalogueNumber(response.data.catalogue_number || '');
            setCustomerPoNumber(response.data.customer_po_number || '');
            setOrderNotes(response.data.notes || '');
            
            if (response.data.vinyl_specs) {
                setEditVinylSpecs(response.data.vinyl_specs);
                
                // If order already has meaningful configuration AND we're not in edit mode,
                // redirect to files page to prevent confusing "reconfigure from scratch" screen
                if (!isEditMode) {
                    const specs = response.data.vinyl_specs;
                    const hasConfig = specs._bundle_id || // Quick Order bundle
                                      response.data.config_flow_id || // Visual Builder flow
                                      (specs.format && specs.quantity); // Legacy/Expert config
                    
                    if (hasConfig) {
                        // Order already configured - redirect to files page
                        navigate(`/orders/${orderId}/files`, { replace: true });
                        return;
                    }
                }
            }
        } catch (error) {
            if (error.response?.status === 403) {
                toast.error(t('common.access_denied', 'Access denied'));
            } else {
                toast.error(t('order.load_failed', 'Failed to load order'));
            }
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveAndContinue = async (specsFromWizard = null) => {
        const specsToSave = specsFromWizard || editVinylSpecs;

        // Quote-gate branch — when the Quick Order review step flagged
        // ``_requires_quote`` on the specs, we route the submission to
        // ``POST /api/orders/request-quote`` which creates the order
        // directly in Quote status. We SKIP the catalogue-number
        // hard-gate for quote submissions because the customer should
        // be able to request a quote BEFORE they have a catalogue
        // number (which they may need admin help to assign).
        const isQuoteRequest = !!specsToSave._requires_quote;
        if (!isQuoteRequest && !catalogueValid) {
            // Hard-gate on catalogue number validity so backend's 400 never surprises the user.
            toast.error(t('configure.fix_catalogue', 'Please fix the catalogue number before continuing.'));
            return;
        }

        setIsSaving(true);
        try {
            // Extract flow versioning data if present
            const flowId = specsToSave._flow_id;
            const flowVersion = specsToSave._flow_version;
            const flowSelections = specsToSave._flow_selections;
            // Customer note from Quick Order review step (optional free text
            // the customer attaches at submit). Persisted to the top-level
            // `notes` field on the order so admin's "Customer Note" sidebar
            // card surfaces it the same way as simple CD/DVD/Bluray orders.
            const customerNote = (orderNotes || '').trim();

            // Remove flow metadata from vinyl_specs before saving
            const cleanSpecs = { ...specsToSave };
            delete cleanSpecs._flow_id;
            delete cleanSpecs._flow_version;
            delete cleanSpecs._flow_selections;
            delete cleanSpecs._customer_note;
            // Drop the client-only quote hints — never persist them to
            // vinyl_specs, the authoritative check happens server-side.
            delete cleanSpecs._requires_quote;
            delete cleanSpecs._quote_gated_skus;
            
            // Ensure vinyl_weight_g (backend integer field) matches vinyl_weight (frontend string).
            // Vinyl-only — CD/DVD/Blu-ray orders shouldn't carry a vinyl weight.
            if (productFamily === 'vinyl' && cleanSpecs.vinyl_weight) {
                cleanSpecs.vinyl_weight_g = parseInt(String(cleanSpecs.vinyl_weight).replace('g', '')) || 140;
            }
            
            // Build the order data
            const orderData = {
                vinyl_specs: cleanSpecs,
            };
            
            // Include flow versioning if present (locks the order to this flow version)
            if (flowId) {
                orderData.config_flow_id = flowId;
            }
            if (flowVersion) {
                orderData.config_flow_version = flowVersion;
            }
            if (flowSelections) {
                orderData.config_selections = flowSelections;
            }
            
            // Always include current title, artist and catalogue number
            if (orderTitle.trim()) {
                orderData.title = orderTitle.trim();
            }
            // Empty string explicitly clears the artist field server-side.
            orderData.artist = orderArtist.trim() || null;
            if (catalogueNumber.trim()) {
                orderData.catalogue_number = catalogueNumber.trim();
            }
            // Include PO number (can be empty string if cleared)
            orderData.customer_po_number = customerPoNumber.trim() || null;

            // Customer note from the Quick Order configure step. Persisted to
            // the order-level `notes` field (the same field admin/production
            // workflows surface) on both create and update, so it's preserved
            // on every save and round-trips on edit. Sending the (possibly
            // empty) string lets an edited note be cleared; the backend
            // normalises "" back to null.
            orderData.notes = customerNote;
            
            let response;
            let newOrderId = orderId;
            
            if (isNewOrder) {
                // CREATE the order now (deferred creation)
                orderData.order_type = 'new_production';
                orderData.product_type = productFamily;
                // ────────────────────────────────────────────────────
                // QUOTE-GATED SUBMISSION PATH
                // ────────────────────────────────────────────────────
                // When the Quick Order review step detected that the
                // selected bundle is quote-gated (manual flag OR
                // procurement BOM trigger), we DO NOT create a Draft.
                // Instead we hit the dedicated request-quote endpoint
                // which:
                //   • revalidates the gate server-side (so a malicious
                //     client can't skip pricing),
                //   • creates the order directly in Quote status,
                //   • uses an idempotent dedupe key so a double-click
                //     cannot create two Quote rows,
                //   • returns the same OrderResponse shape as normal
                //     create.
                // The customer is then routed to the order detail page
                // (/orders/{id}) — NOT /files, because the quote
                // workflow doesn't require production files to be
                // uploaded before pricing.
                if (isQuoteRequest) {
                    response = await ordersApi.requestQuote(
                        orderData,
                        customerNote || null,
                    );
                    newOrderId = response.data.id;
                    setOrder(response.data);
                    toast.success(t('order.quote_requested_toast', 'Quote requested — we\'ll get back to you shortly'));
                    navigate(`/orders/${newOrderId}`);
                    return;
                }
                response = await ordersApi.create(orderData);
                newOrderId = response.data.id;
                setOrder(response.data);
                toast.success(t('order.created_toast', 'Order created'));
            } else {
                // UPDATE existing order
                response = await ordersApi.update(orderId, orderData);
                setOrder(response.data);
                toast.success(t('order.config_saved_toast', 'Configuration saved'));
            }
            
            setEditVinylSpecs(cleanSpecs);
            
            // Navigate to files page
            navigate(`/orders/${newOrderId}/files`);
        } catch (error) {
            toast.error(error.response?.data?.detail || t('order.save_config_failed', 'Failed to save configuration'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleConfigComplete = (newSpecs, priceEstimate) => {
        setEditVinylSpecs(newSpecs);
        handleSaveAndContinue(newSpecs);
    };

    // Delete draft order handler
    const handleDeleteOrder = async () => {
        if (isNewOrder) {
            // For new orders, just navigate back to dashboard
            navigate('/dashboard');
            return;
        }
        
        setIsDeleting(true);
        try {
            await ordersApi.customerDelete(orderId);
            toast.success(t('order.draft_deleted_toast', 'Draft order deleted'));
            navigate('/dashboard');
        } catch (error) {
            const message = error.response?.data?.detail || t('order.delete_draft_failed', 'Failed to delete order');
            toast.error(message);
            setIsDeleting(false);
        }
    };

    // Handle mode change from ProductConfigWizard
    const handleModeChange = useCallback((mode) => {
        console.log('[OrderProductPage] Mode changed to:', mode);
        setConfigMode(mode);
    }, []);

    const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
    // Admins may reconfigure a pending/sent quote in place (no revert-to-Draft
    // needed). Customers stay limited to Draft so they never hit a confusing
    // "quote in progress" save rejection mid-wizard.
    const canEdit = isNewOrder
        || order?.status === 'Draft'
        || (isAdmin && (order?.status === 'Quote' || order?.status === 'Quoted'));

    // Redirect non-draft orders to the order workspace (files page)
    // This hook must be before any conditional returns
    useEffect(() => {
        if (!isNewOrder && order && !canEdit) {
            navigate(`/orders/${orderId}/files`, { replace: true });
        }
    }, [isNewOrder, order, canEdit, orderId, navigate]);

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
                    <LoadingSpinner size="lg" />
                </div>
            </div>
        );
    }

    // For new orders, we don't need an existing order object
    // For existing orders, we need the order to be loaded
    if (!isNewOrder && !order) return null;
    
    // Return null while redirect is happening for non-draft orders
    if (!isNewOrder && !canEdit) {
        return null;
    }

    // ==================== IMMERSIVE MODE (Visual Builder) ====================
    if (isImmersiveMode) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100" data-testid="order-product-page-immersive">
                {/* Full-screen Visual Builder — no top bar, step indicator + summary panel are sufficient */}
                <main className="pb-8">
                    <ProductConfigWizard
                        mode={configMode}
                        productFamily={productFamily}
                        initialSpecs={order?.vinyl_specs || editVinylSpecs}
                        onConfigChange={(newSpecs, priceEstimate) => {
                            setEditVinylSpecs(newSpecs);
                        }}
                        onComplete={handleConfigComplete}
                        onModeChange={handleModeChange}
                        pricingMessage="See your full cost instantly - before you commit"
                        isImmersiveMode={true}
                        orderMetadata={{
                            title: orderTitle || order?.title,
                            catalogueNumber: catalogueNumber || order?.catalogue_number,
                            onTitleChange: setOrderTitle,
                            onCatalogueChange: setCatalogueNumber,
                            // Feed the SHARED backend price engine so the
                            // Visual Builder live total matches the review
                            // page / quote PDF / saved order byte-for-byte.
                            // For NEW orders (no `order` yet) we fall back
                            // to the current user's active company so the
                            // backend can still resolve local currency +
                            // customer discounts — otherwise it defaults
                            // to EUR and the VB shows unconverted values.
                            organization_id: order?.organization_id || user?.active_company_id,
                            product_type: order?.product_type || productFamily,
                            is_reorder: order?.is_reorder || order?.order_type === 'reorder',
                        }}
                    />
                </main>
            </div>
        );
    }

    // ==================== STANDARD MODE (Quick Order, Expert, Mode Selection) ====================
    return (
        <div className="min-h-screen bg-background" data-testid="order-product-page">
            <Header />
            
            <main className={`${configMode === 'quick' ? 'max-w-[1450px]' : 'max-w-4xl'} mx-auto px-4 sm:px-6 lg:px-8 py-6`}>
                {/* Progress Indicator with Delete Button */}
                <div className="mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                        <div className="flex items-center gap-2 text-indigo-600 font-medium">
                            <div className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-semibold">
                                1
                            </div>
                            <span className="hidden sm:inline">{t('order.step_details', 'Order details')}</span>
                        </div>
                        <div className="flex-1 h-px bg-slate-200 mx-2 min-w-[40px]" />
                        <div className="flex items-center gap-2 text-slate-400">
                            <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-xs font-semibold">
                                2
                            </div>
                            <span className="hidden sm:inline">{t('order.step_production_files', 'Production Files')}</span>
                        </div>
                    </div>
                    {/* Delete Draft Button */}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsDeleteDialogOpen(true)}
                        className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                        data-testid="delete-draft-order-btn"
                    >
                        <Trash2 className="h-4 w-4 mr-1" />
                        <span className="hidden sm:inline">{t('order.delete_draft_button', 'Delete Draft')}</span>
                    </Button>
                </div>

                {/* Order Details (Title, Artist, Catalogue, PO) are no longer
                    shown here. The Album / Release Title is captured inside the
                    "Configure Your Vinyl" card (ModeSelection); Artist,
                    Catalogue number and PO number are collected on the final
                    Review Your Order page. Values + validation are preserved
                    via `orderMetadata`. */}

                {/* Product Configuration */}
                <div className="bg-white rounded-xl border border-border p-6">
                    <ProductConfigWizard
                        mode={configMode}
                        productFamily={productFamily}
                        initialSpecs={order?.vinyl_specs || editVinylSpecs}
                        onConfigChange={(newSpecs, priceEstimate) => {
                            setEditVinylSpecs(newSpecs);
                        }}
                        onComplete={handleConfigComplete}
                        onModeChange={handleModeChange}
                        pricingMessage={t('order.pricing_message', 'See your full cost instantly - before you commit')}
                        isImmersiveMode={false}
                        orderTitle={orderTitle}
                        titleInputRef={titleInputRef}
                        orderMetadata={{
                            title: orderTitle,
                            artist: orderArtist,
                            catalogueNumber: catalogueNumber,
                            poNumber: customerPoNumber,
                            catalogueValid: catalogueValid,
                            onTitleChange: setOrderTitle,
                            onArtistChange: setOrderArtist,
                            onCatalogueChange: setCatalogueNumber,
                            onCatalogueValidChange: setCatalogueValid,
                            onPoChange: setCustomerPoNumber,
                            note: orderNotes,
                            onNoteChange: setOrderNotes,
                        }}
                        savedFlowConfig={{
                            flowId: order?.config_flow_id,
                            flowVersion: order?.config_flow_version,
                            selections: order?.config_selections
                        }}
                    />
                </div>
            </main>

            {/* Delete Draft Order Confirmation Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <Trash2 className="h-5 w-5" />
                            {t('order.delete_dialog_title', 'Delete Draft Order?')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('order.delete_dialog_desc', 'Are you sure you want to delete this draft order? This action cannot be undone.')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-slate-600">
                            {t('order.order_prefix', 'Order:')} <span className="font-medium text-slate-900">{order?.title || t('order.untitled', 'Untitled')}</span>
                        </p>
                    </div>
                    <div className="flex justify-end gap-3">
                        <Button
                            variant="outline"
                            onClick={() => setIsDeleteDialogOpen(false)}
                            disabled={isDeleting}
                        >
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteOrder}
                            disabled={isDeleting}
                            data-testid="confirm-delete-draft-order"
                        >
                            {isDeleting ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    {t('common.deleting', 'Deleting...')}
                                </>
                            ) : (
                                t('order.delete_dialog_confirm', 'Delete Order')
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
